import { call, SagaGenerator, select } from "typed-redux-saga";

import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { DocumentModel } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import {
	DataOperation,
	maybeAsyncFnWrapper,
	OverviewEngineFactories,
	ProvideDataResults
} from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/client-extensions";
import { Query } from "@com.mgmtp.a12.dataservices/dataservices-access";

import { ModulfOverviewEngineDataLoader } from "../../app/provider/modulfOverviewEngineDataLoader";
import { assertTrue } from "../../utilities/assert";
import {
	AssignedProcessesDescriptor,
	CombinedAntragListDescriptor,
	ProcessOverviewDescriptor
} from "../../utilities/descriptors";
import { getDocumentModelElement } from "../../utilities/modelElementHelper";
import { MODEL_PATH_TO_STATUS, PATH_TO_STATUS } from "../../utilities/variables/documentModelPaths";

const TECHNICAL_SUFFIX_REGEX = /\[[a-zA-Z0-9_]+\]$/;

/**
 * Overview engine data loader for Fachverfahren main document overviews.
 *
 * Always delegates to the default A12 data loader, but first expands any
 * single-value Status constraint into an OR across all enum values that share
 * the same normalized label (i.e. the same label after stripping the technical
 * FV suffix such as `[fv1_HF2]`).
 */
export const processOverviewLoader: ModulfOverviewEngineDataLoader = {
	*canHandle(params): SagaGenerator<boolean> {
		const { queries, activityId } = params;
		const [query] = queries;

		if (!DataOperation.ListDocuments.Query.isAssignableFrom(query)) {
			return false;
		}

		const activity = yield* select(ActivitySelectors.activityById(activityId));
		return (
			ProcessOverviewDescriptor.isInstance(activity?.descriptor) ||
			CombinedAntragListDescriptor.isInstance(activity?.descriptor) ||
			AssignedProcessesDescriptor.isInstance(activity?.descriptor)
		);
	},

	*provideData(params): ProvideDataResults {
		const { queries, documentModel } = params;
		const [query, ...otherQueries] = queries;
		assertTrue(DataOperation.ListDocuments.Query.isAssignableFrom(query));

		let updatedQuery = query;

		if (query.constraint && hasStatusConstraint(query.constraint)) {
			const statusElement = getDocumentModelElement(documentModel, PATH_TO_STATUS);
			if (statusElement && statusElement.type === "Field" && statusElement.fieldType.type === "EnumerationType") {
				const enumValues = statusElement.fieldType.values;
				updatedQuery = {
					...query,
					constraint: expandStatusConstraints(query.constraint, enumValues)
				};
			}
		}

		return yield* call(maybeAsyncFnWrapper(OverviewEngineFactories.dataLoader.provideData), {
			...params,
			queries: [updatedQuery, ...otherQueries]
		});
	}
};

/**
 * Returns true if the operator tree contains an ExactMatchOperator targeting
 * the Status field.
 */
function hasStatusConstraint(op: Query.Operator): boolean {
	if (Query.ExactMatchOperator.isInstance(op)) {
		return op.field === MODEL_PATH_TO_STATUS;
	}
	if (Query.AndOperator.isInstance(op) || Query.OrOperator.isInstance(op)) {
		return op.operands.some(hasStatusConstraint);
	}
	if (Query.NotOperator.isInstance(op)) {
		return hasStatusConstraint(op.operand);
	}
	return false;
}

/**
 * Recursively walks the operator tree and rewrites any ExactMatchOperator on
 * the Status field into an OR of all enum values whose label (after stripping
 * the technical FV suffix) matches the normalized constraint value.
 *
 * The constraint value is expected to be the localized label text (e.g.
 * `"Genehmigung [RVPSJR2_16]"`). The backend is also queried with label texts,
 * not technical enum keys.
 *
 * If no matching labels are found, or only a single match exists, the operator
 * is returned unchanged.
 */
function expandStatusConstraints(
	op: Query.Operator,
	enumValues: ReadonlyArray<DocumentModel.EnumValue>
): Query.Operator {
	if (Query.ExactMatchOperator.isInstance(op)) {
		if (op.field !== MODEL_PATH_TO_STATUS) {
			return op;
		}

		const normalizedConstraint = stripTechnicalSuffix(String(op.value));

		// Find all enum values whose localized label normalizes to the same text.
		const grouped = enumValues.filter(ev => {
			const labelText = getLabelText(ev.label);
			return labelText !== undefined && stripTechnicalSuffix(labelText) === normalizedConstraint;
		});

		if (grouped.length <= 1) {
			return op;
		}

		// Query by the full label text (including technical suffix) so the backend
		// matches documents regardless of which FV-specific status variant they carry.
		return {
			operator: Query.OPERATORS.OR_OPERATOR,
			operands: grouped
				.map(ev => getLabelText(ev.label))
				.filter((label): label is string => label !== undefined)
				.map((label): Query.ExactMatchOperator => ({
					operator: Query.OPERATORS.EXACT_MATCH_OPERATOR,
					field: MODEL_PATH_TO_STATUS,
					value: label
				}))
		};
	}

	if (Query.AndOperator.isInstance(op) || Query.OrOperator.isInstance(op)) {
		return { ...op, operands: op.operands.map(child => expandStatusConstraints(child, enumValues)) };
	}

	if (Query.NotOperator.isInstance(op)) {
		return { ...op, operand: expandStatusConstraints(op.operand, enumValues) };
	}

	return op;
}

/**
 * Returns the localized label text for an enum value, preferring `de_DE`.
 */
function getLabelText(label: DocumentModel.EnumValue["label"]): string | undefined {
	if (!label || label.length === 0) {
		return undefined;
	}
	return label.find(t => t.locale === "de_DE")?.text ?? label[0].text;
}

/**
 * Strips the technical FV suffix (e.g. `[RVPSJR2_16]`) from a label text and
 * trims the result so values from different FVs can be compared by logical label.
 */
function stripTechnicalSuffix(text: string): string {
	return text.replace(TECHNICAL_SUFFIX_REGEX, "").trim();
}
